import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Plus, Check, Loader2, FlaskConical, ArrowRightLeft, AlertCircle, SearchX } from "lucide-react";
import { getDiagnosticLabs, editDiagnosticLab } from "../../services/diagnosticLabsService";
import { SegmentedControl } from "./primitives";

// "Add member lab" picker for the chain page: server-side search + filter,
// infinite scroll (20 per page), labs already in this chain excluded on the
// server so counts are exact. Several labs can be added without closing it.
const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { value: "active", label: "Active labs" },
  { value: "", label: "All labs" },
];

const LAB_TYPE_LABEL = {
  pathology: "Pathology",
  radiology: "Radiology",
  multi_modality: "Multi-modality",
  collection_center: "Collection centre",
};

const SkeletonRow = () => (
  <li className="flex items-center gap-3 px-4 py-3">
    <span className="w-9 h-9 rounded-xl bg-gray-100 animate-pulse" />
    <span className="flex-1 space-y-1.5">
      <span className="block h-3.5 w-1/3 rounded bg-gray-100 animate-pulse" />
      <span className="block h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
    </span>
    <span className="w-16 h-8 rounded-lg bg-gray-100 animate-pulse" />
  </li>
);

const AddMemberLabPanel = ({ role, chainId, chainName, onAttached, onClose }) => {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("active");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmMoveId, setConfirmMoveId] = useState(null);
  const [added, setAdded] = useState(() => new Set());
  const [rowError, setRowError] = useState({});

  const requestId = useRef(0);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fetchPage = useCallback(async (pageNo) => {
    const id = ++requestId.current;
    if (pageNo === 1) setLoading(true);
    else setLoadingMore(true);
    setError("");
    try {
      const resp = await getDiagnosticLabs(role, {
        page: pageNo, items: PAGE_SIZE, search: debounced, status, exclude_chain: chainId,
      });
      if (id !== requestId.current) return; // a newer search superseded this one
      const data = resp?.msg || {};
      setItems((prev) => (pageNo === 1 ? data.items || [] : [...prev, ...(data.items || [])]));
      setTotal(data.pagination?.total || 0);
      setPage(pageNo);
    } catch (err) {
      if (id === requestId.current) setError(err.message || "Couldn't load labs.");
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [role, chainId, debounced, status]);

  // New search / filter → back to page 1 and the top of the list.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    fetchPage(1);
  }, [fetchPage]);

  const hasMore = items.length < total;

  // Infinite scroll: load the next page when the sentinel enters the list viewport.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading || loadingMore || error) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && fetchPage(page + 1),
      { root: scrollRef.current, rootMargin: "120px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, error, page, fetchPage]);

  const attach = async (lab) => {
    setBusyId(lab.id);
    setRowError((e) => ({ ...e, [lab.id]: "" }));
    try {
      await editDiagnosticLab(role, lab.id, { chain: chainId });
      setAdded((prev) => new Set(prev).add(lab.id));
      setConfirmMoveId(null);
      onAttached?.(lab);
    } catch (err) {
      setRowError((e) => ({ ...e, [lab.id]: err.message || "Couldn't add this lab." }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className="rounded-2xl border border-[#DCEAEA] bg-[#F7FBFB] overflow-hidden"
    >
      {/* Header + controls */}
      <div className="px-4 pt-3.5 pb-3 bg-white border-b border-[#E9F2F2] space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Add labs to {chainName || "this chain"}</p>
            <p className="text-[11px] text-gray-400">Labs already in this chain are not listed</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by lab name, legal name or code (LAB-…)"
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-[#00A6A6]/50 focus:border-transparent transition-colors"
          />
          {query && (
            <button type="button" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <SegmentedControl size="sm" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
          <span className="text-[11px] text-gray-500 tabular-nums">
            {loading ? "Searching…" : `${total.toLocaleString("en-IN")} lab${total === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      {/* Results */}
      <ul ref={scrollRef} className="max-h-[420px] overflow-y-auto divide-y divide-[#EEF5F5] bg-white">
        {loading ? (
          [0, 1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)
        ) : error && items.length === 0 ? (
          <li className="px-4 py-8 text-center">
            <p className="flex items-center justify-center gap-1.5 text-sm text-red-600"><AlertCircle className="w-4 h-4" /> {error}</p>
            <button type="button" onClick={() => fetchPage(1)} className="mt-2 text-xs font-medium text-[#0F4C81] hover:underline">Try again</button>
          </li>
        ) : items.length === 0 ? (
          <li className="px-4 py-10 flex flex-col items-center gap-2 text-center">
            <SearchX className="w-6 h-6 text-gray-300" />
            <p className="text-sm text-gray-500">{debounced ? `No labs match “${debounced}”.` : "No labs available to add."}</p>
            {status === "active" && (
              <button type="button" onClick={() => setStatus("")} className="text-xs font-medium text-[#0F4C81] hover:underline">
                Include inactive labs
              </button>
            )}
          </li>
        ) : (
          <AnimatePresence initial={false}>
            {items.map((lab) => {
              const otherChain = lab.chain?.brand_name;
              const isAdded = added.has(lab.id);
              const busy = busyId === lab.id;
              const confirming = confirmMoveId === lab.id;
              const meta = [lab.city, lab.code, LAB_TYPE_LABEL[lab.lab_type]].filter(Boolean).join(" · "); // ok: drops empty display fragments
              return (
                <motion.li key={lab.id} layout="position" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className={`px-4 py-3 transition-colors ${isAdded ? "bg-emerald-50/50" : confirming ? "bg-amber-50/60" : "hover:bg-[#F7FBFB]"}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-[#EAF2FA] text-[#0F4C81] flex items-center justify-center shrink-0">
                      <FlaskConical className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{lab.brand_name || lab.business_name || "Lab"}</p>
                        {lab.active === false && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-gray-100 text-[10px] font-semibold uppercase text-gray-500">Inactive</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 truncate">{meta || "—"}</p>
                      {otherChain && !isAdded && (
                        <span className="mt-1 inline-flex items-center gap-1 max-w-full px-1.5 py-0.5 rounded-md bg-amber-50 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                          <ArrowRightLeft className="w-3 h-3 shrink-0" />
                          <span className="truncate">Member of {otherChain}</span>
                        </span>
                      )}
                    </div>
                    <div className="shrink-0">
                      {isAdded ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-700">
                          <Check className="w-3.5 h-3.5" /> Added
                        </span>
                      ) : otherChain && !confirming ? (
                        <button type="button" onClick={() => setConfirmMoveId(lab.id)} disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors">
                          <ArrowRightLeft className="w-3.5 h-3.5" /> Move here
                        </button>
                      ) : !confirming ? (
                        <button type="button" onClick={() => attach(lab)} disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0F4C81] border border-[#0F4C81]/20 rounded-lg hover:bg-[#EAF2FA] disabled:opacity-50 transition-colors">
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                          {busy ? "Adding…" : "Add"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {confirming && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                      className="mt-2.5 ml-12 flex flex-wrap items-center justify-between gap-2 overflow-hidden">
                      <p className="text-xs text-amber-800">
                        Move from <span className="font-semibold">{otherChain}</span>? It leaves that chain and any of its test lists.
                      </p>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setConfirmMoveId(null)} disabled={busy}
                          className="px-2.5 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:bg-white">Cancel</button>
                        <button type="button" onClick={() => attach(lab)} disabled={busy}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">
                          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Move to {chainName || "this chain"}
                        </button>
                      </div>
                    </motion.div>
                  )}
                  {rowError[lab.id] && (
                    <p className="mt-1.5 ml-12 flex items-center gap-1 text-[11px] text-red-600">
                      <AlertCircle className="w-3 h-3" /> {rowError[lab.id]}
                    </p>
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>
        )}
        {!loading && items.length > 0 && (
          <li ref={sentinelRef} className="px-4 py-3 text-center text-[11px] text-gray-400">
            {loadingMore ? (
              <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading more…</span>
            ) : error ? (
              <button type="button" onClick={() => fetchPage(page + 1)} className="text-red-600 hover:underline">Couldn't load more — retry</button>
            ) : hasMore ? (
              `Showing ${items.length} of ${total.toLocaleString("en-IN")} — scroll for more`
            ) : (
              `All ${total.toLocaleString("en-IN")} shown`
            )}
          </li>
        )}
      </ul>

      {added.size > 0 && (
        <div className="px-4 py-2.5 bg-white border-t border-[#E9F2F2] flex items-center justify-between gap-2">
          <span className="text-xs text-emerald-700 font-medium">{added.size} lab{added.size === 1 ? "" : "s"} added</span>
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-white bg-[#0F4C81] rounded-lg hover:bg-[#0C3E6B]">Done</button>
        </div>
      )}
    </motion.div>
  );
};

export default AddMemberLabPanel;
